import { useAppDispatch, useAppSelector } from "@/lib/store";
import {
  filterByClass,
  filterByStructure,
  filterByType,
  getProperties,
  resetFilters,
  setSort,
  setPage,
  setPageSize,
  createProperty,
} from "./properties.slice";
import { useEffect, useState } from "react";
import { Property } from "@/models/properties.model";
import { PropertyRepository } from "@/repositories/properties.repository";
import { getGeodecoded } from "@/lib/utils";

export const useProperties = () => {
  const dispatch = useAppDispatch();
  const { list, loading, filters, total } = useAppSelector((state) => state.properties);
  const { type, structure, class: _class, sortBy, page, pageSize, sortDirection } = filters;
  const [creating, setCreating] = useState(false);
  const loadProperties = () =>
    dispatch(
      getProperties({
        ...(type && { type }),
        ...(structure && { structure }),
        ...(_class && { class: _class }),
        sortBy,
        page,
        pageSize,
        sortDirection,
      })
    );

  useEffect(() => {
    loadProperties();
  }, [_class, structure, type, sortBy, page, pageSize, sortDirection]);

  function setPropertyType(type: string) {
    return dispatch(filterByType(type));
  }

  function setPropertyStructure(structure: string) {
    return dispatch(filterByStructure(structure));
  }

  function setPropertyClass(_class: string) {
    return dispatch(filterByClass(_class));
  }

  function resetPropertyFilters() {
    return dispatch(resetFilters());
  }

  function setPropertyPage(page: number) {
    return dispatch(setPage(page));
  }

  function setPropertyPageSize(pageSize: number) {
    return dispatch(setPageSize(pageSize));
  }

  function setPropertySort(sortBy: keyof Property, sortDirection: "asc" | "desc") {
    return dispatch(setSort({ ...filters, sortBy, sortDirection }));
  }

  async function handleCreate(values: Partial<Property>) {
    const locationValues = getGeodecoded(await PropertyRepository.geocodeAddress(values.address as string));
    await dispatch(createProperty({ ...values, ...locationValues })).unwrap();
    setCreating(false);
    loadProperties();
  }

  return {
    list,
    loading,
    filters: {
      ...filters,
      total,
      setPropertyType,
      setPropertyStructure,
      setPropertyClass,
      setPropertyPage,
      setPropertyPageSize,
      setPropertySort,
      resetPropertyFilters,
    },
    creating,
    openCreateModal: () => setCreating(true),
    closeCreateModal: () => setCreating(false),
    handleCreate,
  };
};
